/**
 * The three field shapes both settings pages use.
 *
 * At the `views/v2/` root rather than in either discipline's folder. Settings split in
 * two - `accounting/AccountingSettingsV2.tsx` and
 * `valuation/ValuationSettingsV2.tsx` - and these are the only thing the two halves
 * shared. Putting them in one folder would have made the other import across the
 * boundary `boundary.test.ts` exists to enforce, for three input wrappers.
 *
 * Extracted verbatim from the single file that preceded the split.
 */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
    </label>
  );
}

export const inputClass =
  'mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none';

/**
 * A number that is allowed to be absent.
 *
 * The comparable multiples are genuinely optional - they are cross-checks, not
 * inputs to the valuation - so "no value" has to be expressible and has to
 * survive. NumberField cannot do it: it takes a number, and clearing it would
 * either write NaN or silently keep the old figure, which would make a
 * cross-check impossible to remove once entered.
 */
export function OptionalNumberField({
  label,
  hint,
  value,
  onChange,
  step = 0.5,
}: {
  label: string;
  hint?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  step?: number;
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        type="number"
        step={step}
        value={value ?? ''}
        placeholder="not set"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(null);
            return;
          }
          const parsed = Number(raw);
          if (!Number.isNaN(parsed)) onChange(parsed);
        }}
        className={inputClass}
      />
    </Field>
  );
}

export function NumberField({
  label,
  hint,
  value,
  onChange,
  step = 1,
  suffix,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (value: number) => void;
  step?: number;
  suffix?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-2">
        <input
          type="number"
          step={step}
          value={value}
          onChange={(e) => {
            const parsed = Number(e.target.value);
            // A half-typed number parses as NaN, which would wipe the field
            // and then the model. Keep the last good value instead.
            if (!Number.isNaN(parsed)) onChange(parsed);
          }}
          className={inputClass}
        />
        {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
      </div>
    </Field>
  );
}
